import React from "react";
import { ArrowRight, Check, Star } from "lucide-react";
import { getPlanTypeLabel } from "../utils/PlanUtils";
import { getPlanIcon } from "../utils/planIconMap";
import IconBadge from "./IconBadge";

const MAX_VISIBLE_BENEFITS = 3;

const getBenefitLabels = (plan) => {
  const raw =
    plan?.benefits ?? plan?.planConfig?.benefits ?? plan?.includedBenefits ?? [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((b) => (typeof b === "string" ? b : b?.name ?? b?.label ?? null))
    .filter(Boolean);
};

const PlanCard = ({ plan, active, onSelect, onCardClick }) => {
  if (!plan || typeof plan !== "object") {
    return (
      <div className="rounded-2xl border border-dashed border-[#E8E5F0] bg-[#FAFAFC] p-5 text-center">
        <p className="text-sm text-[#8A8798]">Plan data unavailable</p>
      </div>
    );
  }

  const planName =
    typeof plan.name === "string" && plan.name.trim() ? plan.name : "Untitled plan";

  const planType = getPlanTypeLabel(plan.planType);
  const { icon, tint } = getPlanIcon(plan);
  const isRecommended = plan?.recommended === true;
  const benefits = getBenefitLabels(plan);
  const visibleBenefits = benefits.slice(0, MAX_VISIBLE_BENEFITS);
  const extraBenefitCount = benefits.length - visibleBenefits.length;

  const handleCardClick = () => onCardClick?.(plan);

  const handleViewDetails = (e) => {
    e.stopPropagation();
    onSelect?.(plan);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardClick();
        }
      }}
      aria-pressed={active}
      className={`group relative w-full min-h-[250px] text-left rounded-2xl border p-4 pr-8 cursor-pointer  focus-visible:ring-[#6D28D9] ${
        active
          ? "border-[#6D28D9] bg-[#FAF8FE] shadow-[0_2px_10px_rgba(109,40,217,0.10)]"
          : "border-[#E8E5F0] bg-white hover:border-[#C9BEE8] hover:shadow-[0_4px_16px_rgba(20,19,26,0.07)]"
      }`}
    >
      {/* Top-right status badge: recommended takes priority over selected
          so a card is never showing both at once. */}
      {isRecommended && !active && (
        <span className="absolute -top-2.5 right-4 inline-flex items-center gap-1 rounded-full bg-[#FEF3C7] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#B45309] shadow-sm">
          <Star size={10} strokeWidth={2.5} fill="currentColor" />
          Most popular
        </span>
      )}
      {active && (
        <span className="absolute top-2.5 right-4 inline-flex items-center gap-1 rounded-full bg-[#6D28D9]  text-white shadow-sm">
          <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white/25">
            <Check size={9} strokeWidth={3} />
          </span>
        </span>
      )}

      {/* Chevron affordance on the right edge, hinting the whole card is
          clickable — matches the reference layout. */}
      {/* <span
        aria-hidden="true"
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#C9BEE8] transition-transform duration-150 group-hover:translate-x-0.5"
      >
        <ArrowRight size={16} strokeWidth={2} />
      </span> */}

      {/* Top: icon + name + type */}
      <div className="flex items-start gap-2.5">
        <IconBadge config={{ icon, tint }} size={44} className="h-12 w-12" />
        <div className="min-w-0 flex-1 pt-0.5">
          <h3 className="text-[15px] font-bold text-[#14131A] leading-snug truncate">
            {planName}
          </h3>
          <p className="mt-0.5 text-xs font-medium text-[#8A8798] truncate">
            {planType || "Healthcare plan"}
            {plan.members !== undefined && plan.members !== null && (
              <> · Up to {plan.members} member{Number(plan.members) === 1 ? "" : "s"}</>
            )}
          </p>
        </div>
      </div>

      {/* Price */}
      <div className="mt-3">
        {plan.coveragePrice !== null && plan.coveragePrice !== undefined ? (
          <p className="text-xl font-extrabold text-[#14131A] leading-none">
            ₹{plan.coveragePrice}
            <span className="ml-1 text-[11px] font-semibold text-[#8A8798]">/year</span>
          </p>
        ) : (
          <p className="text-xs font-semibold text-[#8A8798]">Price on request</p>
        )}
      </div>

      {/* Benefit chips */}
      {visibleBenefits.length > 0 && (
  <div className="mt-3 flex flex-wrap items-center gap-1.5">
    {visibleBenefits.map((label, i) => (
      <span
        key={`${label}-${i}`}
        className="inline-flex items-center rounded-full border border-[#A78BFA] bg-[#F3EEFB] px-2 py-1 text-[10px] font-medium text-[#4C1D95]"
      >
        {label}
      </span>
    ))}
    {extraBenefitCount > 0 && (
      <span className="inline-flex items-center rounded-full bg-gradient-to-r from-[#7C3AED] to-[#4C1D95] px-2 py-1 text-[10px] font-bold text-white">
        +{extraBenefitCount} more
      </span>
    )}
  </div>
)}

      {/* Bottom: view details */}
      <div className="mt-3.5 flex items-center justify-between gap-2 pt-3 border-t border-[#EFEDF4]">
        {plan?.planConfig ? (
          <button
            type="button"
            onClick={handleViewDetails}
            className="inline-flex items-center gap-0.5 text-xs font-semibold text-[#6D28D9] hover:gap-1 transition-all duration-150"
            aria-label={`View details for ${planName}`}
          >
            View plan details
            <ArrowRight size={12} strokeWidth={2.25} />
          </button>
        ) : (
          <span />
        )}
      </div>
    </div>
  );
};

export default PlanCard;